import { Identificable } from 'shared/types/commonView';
import type { EventDescriptionPartModel } from 'shared/domain/issue/issueModel';
import { interpolateHexColor } from 'shared/utils/colors';

export enum WorkflowStageIcon {
  description = 'description',
  assignment = 'assignment',
  build = 'build',
  bugReport = 'bugReport',
  security = 'security',
  info = 'info',
  checkCircle = 'checkCircle',
  inventory = 'inventory',
  localShipping = 'localShipping',
  engineering = 'engineering',
  construction = 'construction',
}

export function workflowStageIconForApi(
  icon: WorkflowStageIcon | null | undefined
): WorkflowStageIcon {
  return icon ?? WorkflowStageIcon.info;
}

export const ApprovalType = {
  PARALLEL: 'parallel',
  SEQUENTIAL: 'sequential',
  NONE: null,
} as const;

export type ApprovalType = (typeof ApprovalType)[keyof typeof ApprovalType];

export function isSequentialApproval(
  stage: { approvalType: ApprovalType }
): stage is { approvalType: typeof ApprovalType.SEQUENTIAL } {
  return stage.approvalType === ApprovalType.SEQUENTIAL;
}

export function isParallelApproval(
  stage: { approvalType: ApprovalType }
): stage is { approvalType: typeof ApprovalType.PARALLEL } {
  return stage.approvalType === ApprovalType.PARALLEL;
}

export function hasApprovalRequired(
  stage: { approvalType: ApprovalType }
): stage is { approvalType: typeof ApprovalType.PARALLEL | typeof ApprovalType.SEQUENTIAL } {
  return stage.approvalType !== ApprovalType.NONE;
}

/**
 * Frontend default used when autoApprove is not explicitly provided.
 * This is a UI/domain fallback, not a strict backend payload validator.
 */
export function getDefaultAutoApproveForApprovalType(
  approvalType: ApprovalType
): boolean {
  return isNonApprovalStage({ approvalType });
}

/** Parallel vs sequential badges on workflow lists: each true if any stage uses that mode. */
export function getWorkflowStagesApprovalPresence(
  stages: readonly { approvalType: ApprovalType }[]
): { hasParallel: boolean; hasSequential: boolean } {
  let hasParallel = false;
  let hasSequential = false;

  for (const stage of stages) {
    if (isParallelApproval(stage)) {
      hasParallel = true;
    }
    if (isSequentialApproval(stage)) {
      hasSequential = true;
    }
    if (hasParallel && hasSequential) {
      break;
    }
  }

  return { hasParallel, hasSequential };
}

export function isNonApprovalStage(
  stage: { approvalType: ApprovalType }
): stage is { approvalType: typeof ApprovalType.NONE } {
  return stage.approvalType === ApprovalType.NONE;
}

export interface WorkflowStageApproverOutDto {
  userId: string;
  order: string;
}

export interface WorkflowStageOutDto extends Identificable {
  name: string;
  description?: string;
  /** Absent or null: show stage order number in the UI instead of a pictogram. */
  icon: WorkflowStageIcon | null;
  order: string;
  approvalType: ApprovalType;
  approvers: WorkflowStageApproverOutDto[];
  /**
   * Number of approving decisions required for the stage to advance.
   * `null` when `approvalType` is `NONE` (no approval needed).
   * Otherwise an integer in [1, approvers.length].
   */
  requiredApprovals: number | null;
  deadlineDays?: number;
  autoApprove: boolean;
  /** When true, the stage was soft-deleted and should not be used for operations. */
  deleted?: boolean;
  modifiedAt?: string;
}

type WorkflowStageDisplayNameSource = Partial<WorkflowStageOutDto> & {
  label?: string;
  internalName?: string;
  stage?: string;
};

export function getWorkflowStageDisplayName(
  stage: WorkflowStageDisplayNameSource | undefined
): string {
  if (!stage) {
    return '';
  }

  const candidates = [
    stage.name,
    stage.label,
    stage.internalName,
    stage.stage,
  ];

  return candidates.find(
    (candidate): candidate is string =>
      typeof candidate === 'string' && candidate.trim().length > 0
  ) ?? '';
}

export interface IssueWorkflowOutDto extends Identificable {
  name: string;
  stages: WorkflowStageOutDto[];
  isLocked?: boolean;
}

export interface WorkflowCreateInDto {
  name: string;
}

export interface WorkflowUpdateInDto {
  name: string;
}

export interface WorkflowStageApproverInDto {
  userId: string;
}

export interface StageCreateInDto {
  name: string;
  icon: WorkflowStageIcon | null;
  beforeId?: string;
  afterId?: string;
  approvalType: ApprovalType;
  approvers?: WorkflowStageApproverInDto[];
  /** When omitted the backend defaults n to `approvers.length`. */
  requiredApprovals?: number | null;
}

export interface StageUpdateInDto {
  name?: string;
  description?: string;
  icon?: WorkflowStageIcon | null;
  approvalType?: ApprovalType;
  /** Must align with `approvalType` when sent (e.g. `true` for automatic approval). */
  autoApprove?: boolean;
  approvers?: WorkflowStageApproverInDto[];
  /** Pass `null` to clear (e.g. when approvalType becomes NONE), or omit to leave unchanged. */
  requiredApprovals?: number | null;
  deadlineDays?: number;
}

export interface StageReorderInDto {
  order: {
    beforeId?: string;
    afterId?: string;
  };
}

export const STAGE_COLORS_BY_INDEX: readonly string[] = Object.freeze([
  '#FF9800',
  '#FFC107',
  '#C0CA33',
  '#7CB342',
]);

export function getStageColorByIndex(index: number, totalStages?: number): string {
  const total = totalStages ?? 4;
  const firstColor = STAGE_COLORS_BY_INDEX[0];
  const lastColor = STAGE_COLORS_BY_INDEX[STAGE_COLORS_BY_INDEX.length - 1];

  if (total <= 4) {
    return STAGE_COLORS_BY_INDEX[Math.min(index, STAGE_COLORS_BY_INDEX.length - 1)];
  }

  if (index === 0) return firstColor;
  if (index >= total - 1) return lastColor;

  const ratio = index / (total - 1);
  return interpolateHexColor(firstColor, lastColor, ratio);
}

/**
 * Determines whether a stage circle should display a check icon.
 * Returns true if the stage is done, or if it's the current stage AND the last stage.
 */
export function shouldShowStageCheckIcon(
  index: number,
  stageCount: number,
  isCurrent: boolean,
  isDone: boolean
): boolean {
  const isLastStage = index === stageCount - 1;
  return isDone || (isCurrent && isLastStage);
}

export const WorkflowDecision = {
  APPROVED: 'approved',
  REJECTED: 'rejected',
} as const;

export type WorkflowDecisionType = (typeof WorkflowDecision)[keyof typeof WorkflowDecision];

export function isApprovedDecision(
  decision: { type: WorkflowDecisionType }
): decision is { type: typeof WorkflowDecision.APPROVED } {
  return decision.type === WorkflowDecision.APPROVED;
}

export function isRejectedDecision(
  decision: { type: WorkflowDecisionType }
): decision is { type: typeof WorkflowDecision.REJECTED } {
  return decision.type === WorkflowDecision.REJECTED;
}

export interface WorkflowDecisionInDto {
  type: WorkflowDecisionType;
  decisionOnStageId: string;
  description?: EventDescriptionPartModel[];
}

export interface WorkflowDecisionOutDto {
  _id: string;
  stageId: string;
  createdBy: string;
  createdAt: string;
  type: WorkflowDecisionType;
  description?: EventDescriptionPartModel[] | null;
}

export type ApprovalRestrictionReason =
  | 'previous_no_decision'
  | 'previous_rejected'
  | null;

export interface CanApproverMakeDecisionResult {
  canMake: boolean;
  reason: ApprovalRestrictionReason;
}

export interface CanApproverMakeDecisionArgs {
  isCurrentUser: boolean;
  isSequential: boolean;
  approverIndex: number;
  seqApprovedCount: number;
  decisions: WorkflowDecisionOutDto[];
  stage: WorkflowStageOutDto;
}
